import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  input,
  Input,
  output,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { catchError, concatMap, filter, forkJoin, map, of, take, withLatestFrom } from 'rxjs';

import { ConnectorTarget, DatasetInfo } from '@blaze-shared/api-contracts-domain';
import { blazeActions } from '@blaze-shared/app-state-data-access';
import { ConnectorsCollectionService } from '@blaze-shared/connectors-data-access';
import {
  type ConnectorConfiguration,
  connectorServiceIcons,
  ConnectorTemplate,
} from '@blaze-shared/connectors-domain';
import { TablesApiService, TablesCollectionService } from '@blaze-shared/datasets-data-access';
import {
  BlazeTable,
  type BlazeTableConnector,
  BlazeTableConnectorSets,
  LinkedBlazeTableConnector,
} from '@blaze-shared/datasets-domain';
import {
  LocalStorageService,
  NotificationService,
  StorageKey,
} from '@blaze-shared/platform-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { DatasetSchema } from '@blaze-shared/schema-domain';
import { DialogService } from '@blaze-shared/shared-feature';
import { TooltipDirective } from '@blaze-shared/shared-ui';

import { DatasetGroupComponent } from '../dataset-group/dataset-group.component';
import { DatasetToggleComponent } from '../dataset-toggle/dataset-toggle.component';
import { ConnectorDialogData } from '../models/data-connector-dialog';
import { DataConnectorsMenuPages } from '../models/data-connector-menu';
import { isRestApiConnector } from '../utils';

@Component({
  selector: 'blaze-connector-menu-item',
  imports: [
    CommonModule,
    DatasetGroupComponent,
    DatasetToggleComponent,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    TooltipDirective,
  ],
  templateUrl: './connector-menu-item.component.html',
  styleUrls: ['./connector-menu-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectorMenuItemComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly connectorsCollService = inject(ConnectorsCollectionService);
  private readonly dialogService = inject(DialogService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly notificationService = inject(NotificationService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  private readonly tablesApiService = inject(TablesApiService);
  private readonly tablesCollService = inject(TablesCollectionService);

  projectGroupId = input<string>('');

  create = output<ConnectorDialogData>();
  editConnector = output<ConnectorDialogData>();
  editDatasets = output<ConnectorDialogData>();
  previewDataset = output<ConnectorDialogData>();
  refreshDatasets = output<void>();

  @Input() connector?: ConnectorConfiguration;
  @Input() linkedTableConnector: LinkedBlazeTableConnector | undefined;
  @Input() projectId = '';
  @Input() template: ConnectorTemplate | undefined;
  @Input({ required: true }) page: DataConnectorsMenuPages;

  private _tableConnector: BlazeTableConnectorSets | undefined;
  protected isExpanded = signal(false);

  @Input() set tableConnector(val: BlazeTableConnector) {
    if (!val) {
      this._tableConnector = undefined;
      return;
    }

    const sortedTables = val.tables.toSorted((a, b) => a.name.localeCompare(b.name));
    const tableSets = new Map<string, BlazeTable[]>();
    const tablesWithoutSet: BlazeTable[] = [];
    for (const tab of sortedTables) {
      if (tab.tableSet) {
        tableSets.set(tab.tableSet, [...(tableSets.get(tab.tableSet) || []), tab]);
      } else {
        tablesWithoutSet.push(tab);
      }
    }
    const name = val.name;
    this._tableConnector = {
      name,
      tables: tablesWithoutSet,
      sets: Array.from(tableSets.entries())
        .toSorted(([nameA], [nameB]) => nameA.localeCompare(nameB))
        .map(([name, tables]) => {
          return {
            isAccessLogs: tables.some((t) => t.tableSet === 'Access Logs'),
            isChangeTracking: tables.some((t) => t.isChangeTrackingTable),
            isSystemTable: tables.some((t) => t.isBlazeTable),
            name,
            tables,
          };
        }),
    };
  }
  get tableConnector(): BlazeTableConnectorSets | undefined {
    return this._tableConnector;
  }

  @HostBinding('attr.data-cy') get testId(): string {
    return `ConnectorMenuItem:${this.connectorTarget || ''}`;
  }

  /**
   * Checks whether current menu item displays a configured connector.
   * @returns false if it's a template, true if it's a configured connector.
   */
  get hasConfiguredConnections(): boolean {
    return (
      !!this.connector?.datasets?.length ||
      !!this.connector?.actions?.length ||
      !!this.connector?.customApiDefinition ||
      !!this.connector?.name ||
      !!this.tableConnector?.tables?.length ||
      !!this.tableConnector?.sets?.length ||
      !!this.linkedTableConnector?.tables.length
    );
  }

  get needsConfigurationForEnvironment(): boolean {
    return !!this.connector && !this.connector.isInternal && this.connector.isConfigured === false;
  }

  get isAddIconVisible(): boolean {
    return (
      (!this.tableConnector && !this.hasConfiguredConnections) || !!this.tableConnector // Table connector should have it always visible
    );
  }

  get isMenuEditConnectorVisible(): boolean {
    if (this.linkedTableConnector) {
      return true;
    }

    if (!this.connector) {
      return false;
    }

    return !!this.connector.isEditable;
  }

  get menuEditConnectorLabel(): string {
    return this.connector?.isConfigured ? 'Edit connection' : 'Configure';
  }

  get isMenuEditDatasetsVisible(): boolean {
    const { connectorType, isConfigured = false } = this.connector || {};
    return connectorType === 'DataSource' && !this.linkedTableConnector && isConfigured;
  }

  get canBeExpanded(): boolean {
    if (this.page === DataConnectorsMenuPages.AddNew) {
      return false;
    }

    return (
      (this.connector?.datasets?.length || 0) >= 1 || // Connector has more than one dataset
      (this.connector?.actions?.length || 0) >= 1 || // Connector has more than one action
      !!this.tableConnector?.tables?.length || // Blaze Table connector has defined tables
      !!this.tableConnector?.sets?.length || // Blaze Table connector has defined sets
      !!this.linkedTableConnector?.tables.length
    );
  }

  get connectorTarget(): ConnectorTarget | undefined {
    if (this.tableConnector) {
      return 'BlazeTable';
    }

    if (this.linkedTableConnector) {
      return 'BlazeTableLinked';
    }

    if (isRestApiConnector(this.template) || isRestApiConnector(this.connector)) {
      return 'RestApi';
    }

    if (this.template?.target) {
      return this.template.target;
    }

    if (this.connector?.target) {
      return this.connector.target;
    }

    return undefined;
  }

  get isMoreIconVisible(): boolean {
    return !this.tableConnector && this.hasConfiguredConnections && !this.connector?.isInternal;
  }

  get label(): string {
    // Configured connections
    if (this.hasConfiguredConnections) {
      if (this.tableConnector) {
        return this.tableConnector.name;
      }
      if (this.linkedTableConnector) {
        const { name, projectName } = this.linkedTableConnector;
        const label = 'Blaze Table';
        return name || projectName ? `${projectName}` : label;
      }
      return this.connector?.name ?? '';
    }
    // No configured connections
    if (this.tableConnector) {
      return this.tableConnector.name;
    }
    if (this.linkedTableConnector) {
      return this.linkedTableConnector.name;
    }
    if (this.template?.displayName) {
      return this.template?.displayName;
    }
    if (this.template?.target) {
      return this.template?.target;
    }
    if (this.connector?.name) {
      return this.connector?.name;
    }
    return '';
  }

  get menuItemIcon(): string {
    let target = this.connectorTarget;
    if (this.connector?.configuration?.target) {
      target = this.connector.configuration.target as ConnectorTarget;
    }
    const icon = target ? connectorServiceIcons[target] : '';
    return icon || 'icon_dummy_32';
  }

  protected onConvertToSharedWithinGroup(): void {
    this.dialogService
      .openConfirmDialog({
        title: 'Share Tables with Project Group',
        description: 'Share all datasets from this group with other projects in the group?',
        headerIcon: 'globe',
        headerIconColor: 'text-primary-lighter',
        headerIconSize: '16',
        cancelLabel: 'Cancel',
        confirmLabel: 'Share',
      })
      .afterClosed()
      .pipe(
        filter((shouldShare) => !!shouldShare),
        concatMap(() => {
          const tables = this.linkedTableConnector?.tables ?? [];
          const requests = tables.map((dataset) => {
            return this.tablesApiService
              .postLinkConvertToShared(this.projectId || '', dataset.id || '')
              .pipe(
                catchError((err) => {
                  this.notificationService.showError(err);
                  return of(null);
                }),
              );
          });
          return forkJoin(requests);
        }),
      )
      .subscribe(() => {
        this.refreshDatasets.emit();
      });
  }

  protected onCreateConnector(): void {
    // Make sure we don't associate external account with connector when creating it through the menu
    this.localStorageService.delete(StorageKey.IsExternalAccountConnector);
    this.create.emit({
      template: this.template,
      connector: this.connector,
    });
  }

  protected onDeleteConnector(): void {
    if (this.linkedTableConnector) {
      this.unlinkConnector();
    } else if (confirm(`Do you want to delete ${this.label} connector?`)) {
      this.connectorsCollService.delete(this.connector?.id || '');
    }
  }

  protected onDeleteTable(table: BlazeTable): void {
    this.store.dispatch(blazeActions.deleteTable({ tableId: table.id }));
  }

  protected onEditConnector(): void {
    // Make sure we don't associate external account with connector when creating it through the menu
    this.localStorageService.delete(StorageKey.IsExternalAccountConnector);
    this.editConnector.emit({
      connector: this.connector,
      template: this.template,
      linkedTableConnector: this.linkedTableConnector,
    });
  }

  protected onEditDatasets(): void {
    this.editDatasets.emit({
      connector: this.connector,
      template: this.template,
    });
  }

  protected onEditTable(table: BlazeTable): void {
    this.editConnector.emit({ table });
  }

  protected onPreview(datasetToPreview?: DatasetInfo): void {
    const datasetId = datasetToPreview?.id ?? this.connector?.datasets?.[0]?.id;
    if (!datasetId) {
      return;
    }

    this.schemaService
      .obtainSchema<DatasetSchema>(datasetId)
      .pipe(take(1))
      .subscribe((dataset) => {
        this.previewDataset.emit({
          connector: this.connector,
          previewDataset: dataset,
          template: this.template,
        });
      });
  }

  protected onRefreshTables(): void {
    this.refreshDatasets.emit();
  }

  protected onShowMoreOptions(event: MouseEvent): void {
    event.stopPropagation();
  }

  protected toggleConnectionsVisibility(): void {
    if (this.canBeExpanded) {
      this.isExpanded.set(!this.isExpanded());
    }
  }

  protected updateMenu(): void {
    // Update menu after changing state
    this.cdr.detectChanges();
  }

  private unlinkConnector(): void {
    const connectorToUnlink = this.linkedTableConnector;
    if (!connectorToUnlink) {
      throw new Error('No linked connector to unlink');
    }

    if (confirm(`Do you want to unlink tables from project ${connectorToUnlink.projectName}?`)) {
      this.tablesApiService
        .postLinkProjectTables(this.projectId, connectorToUnlink.projectId, [])
        .pipe(
          withLatestFrom(this.tablesCollService.entities$),
          map(([_, tables]) =>
            tables.filter((table) => table.projectId === connectorToUnlink.projectId),
          ),
        )
        .subscribe((tablesToRemove) => {
          this.tablesCollService.removeManyFromCache(tablesToRemove);
        });
    }
  }
}
